import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Loader2, ChevronDown, ArrowLeft, Trash2, Mic, Square, Paperclip, X, Check, CheckCheck, Clock3, AlertCircle, RotateCcw, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { ENDPOINTS, getAuthHeaders, handleAuthError } from './config';
import useVoiceRecording from './hooks/useVoiceRecording';
import useDialogA11y from './useDialog';

// WhatsApp-style typing indicator
const TypingIndicator = () => (
  <div className="flex items-end gap-1">
    <div className="wa-bubble-in relative px-3 py-2 rounded-lg rounded-bl-none">
      <div className="flex space-x-1">
        <div className="w-2 h-2 bg-[#667781] rounded-full animate-bounce [animation-delay:-0.3s]"></div>
        <div className="w-2 h-2 bg-[#667781] rounded-full animate-bounce [animation-delay:-0.15s]"></div>
        <div className="w-2 h-2 bg-[#667781] rounded-full animate-bounce"></div>
      </div>
    </div>
  </div>
);

// Format timestamp
const formatTime = (date) => {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const dayKey = (date) => {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;
  return `${parsed.getFullYear()}-${parsed.getMonth()}-${parsed.getDate()}`;
};

const formatDay = (date) => {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '';

  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const dateStart = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  const daysAgo = Math.round((todayStart - dateStart) / 86400000);
  if (daysAgo === 0) return 'Today';
  if (daysAgo === 1) return 'Yesterday';
  return parsed.toLocaleDateString([], { month: 'short', day: 'numeric', year: parsed.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
};

const createClientId = () => `chat-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const getTimeGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 6) return "Can't sleep baby? I'm here for you ❤️";
  if (hour < 12) return "Good morning gorgeous! How are you today? ❤️";
  if (hour < 17) return "Hey baby! What's up? ❤️";
  if (hour < 21) return "Hey princess! How was your day? ❤️";
  return "Hey baby, winding down for the night? ❤️";
};

const buildInitialConversation = () => [
  {
    clientId: createClientId(),
    role: "system",
    content: "You are AI Navjeev - a digital companion for Kate when Nav is busy."
  },
  {
    clientId: createClientId(),
    role: "assistant",
    content: getTimeGreeting(),
    timestamp: new Date().toISOString()
  }
];

// Image compression utility
const compressImage = async (file, maxSize = 1024) => {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    const timeout = setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Image load timed out'));
    }, 10000);

    img.onload = () => {
      clearTimeout(timeout);
      try {
        const canvas = document.createElement('canvas');
        let { width, height } = img;

        if (width > maxSize || height > maxSize) {
          if (width > height) {
            height = (height / width) * maxSize;
            width = maxSize;
          } else {
            width = (width / height) * maxSize;
            height = maxSize;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(objectUrl);
          if (!blob) {
            reject(new Error('Canvas toBlob returned null'));
            return;
          }
          resolve(blob);
        }, 'image/jpeg', 0.8);
      } catch (err) {
        URL.revokeObjectURL(objectUrl);
        reject(err);
      }
    };

    img.onerror = () => {
      clearTimeout(timeout);
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to decode image'));
    };

    img.src = objectUrl;
  });
};

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// Helper to extract image base64 from message content array
const getImageFromContent = (content) => {
  if (!Array.isArray(content)) return null;
  const imageItem = content.find(item => item.type === 'image_url');
  return imageItem?.image_url?.url || null;
};

// Helper to extract audio base64 from message content array
const getAudioFromContent = (content) => {
  if (!Array.isArray(content)) return null;
  const audioItem = content.find(item => item.type === 'input_audio');
  return audioItem?.input_audio?.data || null;
};

// Helper to extract audio format from message content array
const getAudioFormatFromContent = (content) => {
  if (!Array.isArray(content)) return null;
  const audioItem = content.find(item => item.type === 'input_audio');
  return audioItem?.input_audio?.format || null;
};

// Voice message player component with audio playback and expandable transcription
const VoiceMessagePlayer = ({ audioBase64, duration, transcription, audioFormat }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!audioBase64) {
    return <span className="text-sm text-[#667781]">Voice message ({duration}s)</span>;
  }

  // Use correct MIME type based on format; fallback to webm for old localStorage messages
  const mimeType = audioFormat === 'wav' ? 'audio/wav' : 'audio/webm';
  const audioSrc = `data:${mimeType};base64,${audioBase64}`;

  return (
    <div className="voice-message">
      <div className="flex items-center gap-2">
        <audio src={audioSrc} controls className="h-8 max-w-[200px]" controlsList="nodownload" />
        <span className="text-xs text-[#667781]">{duration}s</span>
      </div>
      {transcription && (
        <div className="mt-1">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-xs text-[#00A884] hover:underline flex items-center gap-1"
          >
            {isExpanded ? '\u25BC' : '\u25B6'} Transcription
          </button>
          {isExpanded && (
            <p className="text-xs text-[#667781] italic mt-1 pl-2 border-l-2 border-[#00A884]">
              "{transcription}"
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const OutgoingMessageStatus = ({ status }) => {
  if (status === 'sending') {
    return <Clock3 className="h-3.5 w-3.5 text-[#667781]" aria-label="Sending" />;
  }
  if (status === 'failed') {
    return <AlertCircle className="h-4 w-4 text-[#D92D20]" aria-label="Not sent" />;
  }
  if (status === 'delivered') {
    return <CheckCheck className="h-4 w-4 text-[#53BDEB]" aria-label="Delivered" />;
  }
  return <Check className="h-4 w-4 text-[#667781]" aria-label="Sent" />;
};

const ModernChatInterface = () => {
  const navigate = useNavigate();

  const [conversation, setConversation] = useState(() => {
    const saved = localStorage.getItem('chat_history');
    if (saved) {
      try {
        return JSON.parse(saved).map((message) => ({
          ...message,
          clientId: message.clientId || createClientId(),
          ...(message.role === 'user' && !message.status ? { status: 'sent' } : {})
        }));
      } catch (e) {
        console.error("Failed to parse chat history", e);
      }
    }
    return buildInitialConversation();
  });

  const [inputMessage, setInputMessage] = useState(() => localStorage.getItem('chat_draft') || '');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesViewportRef = useRef(null);
  const textareaRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const {
    isRecording,
    formattedDuration,
    startRecording,
    stopRecording,
    cancelRecording
  } = useVoiceRecording();

  const [selectedImage, setSelectedImage] = useState(null);
  const fileInputRef = useRef(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  // The clear-chat confirmation answers to the house dialog contract in
  // useDialog.js -- focus in, inert background, focus trap, Escape/backdrop
  // close, focus restored to the trash button -- not a hand-rolled modal.
  const clearDialogRef = useRef(null);
  // Bumped by Clear chat. The staggered reply loop snapshots it when the
  // response arrives and re-checks before every append, so bubbles from a
  // conversation the user just discarded cannot resurrect into the new one.
  const conversationEpochRef = useRef(0);
  const chatBackgroundRef = useRef(null);
  const clearDialogKeyDown = useDialogA11y({
    open: showClearConfirm,
    onClose: () => setShowClearConfirm(false),
    dialogRef: clearDialogRef,
    backgroundRef: chatBackgroundRef,
  });
  const [reactions, setReactions] = useState(() => {
    try { return JSON.parse(localStorage.getItem('chat_reactions') || '{}'); } catch { return {}; }
  });
  const [reactionPickerIdx, setReactionPickerIdx] = useState(null);
  const lastTapRef = useRef({});

  useEffect(() => {
    localStorage.setItem('chat_reactions', JSON.stringify(reactions));
  }, [reactions]);

  // Keep an unfinished thought through a refresh or an accidental navigation.
  // The draft is removed as soon as the composer is genuinely empty.
  useEffect(() => {
    if (inputMessage) localStorage.setItem('chat_draft', inputMessage);
    else localStorage.removeItem('chat_draft');
  }, [inputMessage]);

  // WhatsApp's composer grows with the message, but stops before it can crowd
  // the conversation off screen. text-base also avoids iOS input zoom.
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = '0px';
    const nextHeight = Math.min(textarea.scrollHeight, 120);
    textarea.style.height = `${Math.max(nextHeight, 24)}px`;
    textarea.style.overflowY = textarea.scrollHeight > 120 ? 'auto' : 'hidden';
  }, [inputMessage]);

  const handleMessageDoubleTap = (index) => {
    const now = Date.now();
    const last = lastTapRef.current[index] || 0;
    if (now - last < 300) {
      setReactionPickerIdx(reactionPickerIdx === index ? null : index);
      lastTapRef.current[index] = 0;
    } else {
      lastTapRef.current[index] = now;
    }
  };

  const addReaction = (msgIndex, emoji) => {
    setReactions(prev => ({ ...prev, [msgIndex]: emoji }));
    setReactionPickerIdx(null);
  };

  // Track visual viewport height for iOS keyboard handling
  useEffect(() => {
    const updateHeight = () => {
      const vh = window.visualViewport?.height || window.innerHeight;
      document.documentElement.style.setProperty('--app-height', `${vh}px`);
      // Scroll messages to bottom when keyboard opens/closes
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      });
    };
    updateHeight();
    window.visualViewport?.addEventListener('resize', updateHeight);
    window.addEventListener('resize', updateHeight);

    // Defense-in-depth: snap page scroll back to origin on iOS
    const handleResize = () => {
      if (!window.visualViewport) return;

      const vh = window.visualViewport.height;
      document.documentElement.style.setProperty('--app-height', `${vh}px`);
      // Force window to top to prevent "white space" under the keyboard
      window.scrollTo(0, 0);

      // Scroll messages to bottom if keyboard opened
      requestAnimationFrame(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
        }
      });
    };

    window.visualViewport?.addEventListener('resize', handleResize);
    window.visualViewport?.addEventListener('scroll', handleResize);

    // Apply strict body lock when entering chat
    document.body.classList.add('chat-body-lock');
    document.documentElement.classList.add('chat-body-lock');

    handleResize(); // Init

    return () => {
      window.visualViewport?.removeEventListener('resize', updateHeight);
      window.removeEventListener('resize', updateHeight);
      window.visualViewport?.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('scroll', handleResize);

      // Remove strict body lock when leaving chat to restore scrolling on other pages
      document.body.classList.remove('chat-body-lock');
      document.documentElement.classList.remove('chat-body-lock');
    };
  }, []);

  // Fix 1: Strip non-serializable fields (like blob URLs) before saving to localStorage
  useEffect(() => {
    const toSave = conversation.map(msg => ({
      clientId: msg.clientId,
      role: msg.role,
      content: msg.content,
      timestamp: msg.timestamp,
      displayText: msg.displayText,
      transcription: msg.transcription,
      audioDuration: msg.audioDuration,
      status: msg.status,
      ...(msg.type && { type: msg.type })
      // imagePreview intentionally omitted - blob URLs don't persist across page reloads
    }));
    localStorage.setItem('chat_history', JSON.stringify(toSave));
  }, [conversation]);

  // Fetch server-side history on mount (supplements localStorage)
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await fetch(`${ENDPOINTS.CHAT_HISTORY}?limit=5000`, { headers: getAuthHeaders() });
        if (handleAuthError(res)) return;
        if (!res.ok) return;
        const serverMessages = await res.json();

        if (serverMessages.length > 0) {
          // Always prioritize server history for cross-device sync
          // Reconstruct the conversation: System Prompt + Server Messages
          const hydrated = [
            buildInitialConversation()[0], // Keep the system personality
            // We omit the getTimeGreeting() if we have real history, 
            // or we could append it? Usually better to just show history.
            ...serverMessages.map(m => ({
              clientId: `server-${m.id || createClientId()}`,
              role: m.role,
              content: m.content, // This handles both text strings and array content (multimodal)
              timestamp: m.timestamp,
              ...(m.role === 'user' && { status: 'delivered' }),
              metadata: m.metadata,
              // Restore UI helper fields if possible, or they default to undefined
              ...(m.metadata?.has_audio && {
                displayText: `Voice message (${m.metadata.audio_duration || '?'}s)`,
                audioDuration: m.metadata.audio_duration
                // Note: Audio blob data isn't re-fetched here, just the record. 
                // If we wanted playable audio, we'd need to fetch binaries or store URLs.
                // For now, this syncs the text/transcript representation.
              }),
              ...(m.metadata?.transcription && { transcription: m.metadata.transcription }),
              ...(m.metadata?.has_image && { displayText: 'Sent an image' })
            }))
          ];
          // Server history is the sync source for everything that REACHED the
          // server -- but a failed or still-sending local message never did.
          // A blind replace here deleted exactly the message whose Retry
          // button the user still needed, and the next localStorage save made
          // that deletion permanent. Carry the unsent ones over, and mark a
          // message caught mid-flight by the reload as failed: its request
          // died with the old page, so "sending" would be a lie.
          setConversation((previous) => {
            const unsent = previous
              .filter((m) => m.role === 'user' && (m.status === 'failed' || m.status === 'sending'))
              .map((m) => ({ ...m, status: 'failed' }));
            return [...hydrated, ...unsent];
          });
        }
      } catch (e) {
        // Non-critical — localStorage is the fallback if offline
        console.debug('Server history fetch failed:', e);
      }
    };
    fetchHistory();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollToBottom = (behavior = 'smooth') => {
    isNearBottomRef.current = true;
    setShowScrollButton(false);
    messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
  };

  const handleScroll = (e) => {
    const nearBottom =
      e.currentTarget.scrollHeight - e.currentTarget.scrollTop - e.currentTarget.clientHeight < 120;
    isNearBottomRef.current = nearBottom;
    setShowScrollButton(!nearBottom);
  };

  // New messages follow the reader only while they are already near the end.
  // If they have scrolled back, preserve their place and offer Jump to latest.
  useEffect(() => {
    if (isNearBottomRef.current) {
      scrollToBottom('smooth');
    } else {
      setShowScrollButton(true);
    }
  }, [conversation, isTyping]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (e) => {
    setInputMessage(e.target.value);
  };

  const handleComposerKeyDown = (event) => {
    // Match WhatsApp Web: Enter sends, Shift+Enter creates a new line.  IME
    // composition must finish before Enter is treated as an instruction.
    if (
      event.key === 'Enter'
      && !event.shiftKey
      && !event.nativeEvent?.isComposing
      && !isLoading
      && !isRecording
    ) {
      event.preventDefault();
      event.currentTarget.form?.requestSubmit();
    }
  };

  // Fix 5: Use React state for confirmation modal instead of window.confirm()
  const handleClearHistory = () => {
    setShowClearConfirm(true);
  };

  const confirmClearHistory = () => {
    // Fire-and-forget: send transcript to backend for memory analysis + email summary
    const userAssistantMessages = conversation.filter(
      msg => msg.role === 'user' || msg.role === 'assistant'
    );
    // Only send if there's a real conversation (more than just the initial greeting)
    if (userAssistantMessages.length > 1) {
      const transcript = userAssistantMessages.map(msg => ({
        role: msg.role,
        content: msg.displayText || (typeof msg.content === 'string' ? msg.content : '[multimodal content]'),
        timestamp: msg.timestamp || '',
        transcription: msg.transcription || null
      }));
      fetch(ENDPOINTS.CHAT_CLEAR, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ transcript })
      }).catch(err => console.warn('Chat clear processing failed (non-critical):', err));
    }

    conversationEpochRef.current += 1;
    setConversation(buildInitialConversation());
    localStorage.removeItem('chat_history');
    toast.success("Conversation cleared");
    setShowClearConfirm(false);
  };

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    try {
      let processedFile;
      try {
        processedFile = await compressImage(file, 1024);
      } catch (compressErr) {
        console.warn('Compression failed, using original file:', compressErr);
        processedFile = file;
      }

      const base64 = await fileToBase64(processedFile);
      const preview = URL.createObjectURL(processedFile);

      setSelectedImage({ file: processedFile, preview, base64 });
    } catch (err) {
      console.error('Error processing image:', err);
      toast.error('Failed to process image');
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeSelectedImage = () => {
    if (selectedImage?.preview) {
      URL.revokeObjectURL(selectedImage.preview);
    }
    setSelectedImage(null);
  };

  const handleMicClick = async () => {
    if (isRecording) {
      const audioData = await stopRecording();
      if (audioData) {
        await sendVoiceMessage(audioData);
      }
    } else {
      try {
        await startRecording();
      } catch (err) {
        toast.error('Could not access microphone');
      }
    }
  };

  const markMessageStatus = (clientId, status) => {
    setConversation((previous) => previous.map((message) => (
      message.clientId === clientId ? { ...message, status } : message
    )));
  };

  const conversationForApi = (messages) => {
    const sendable = messages.filter((message) => (
      !(message.role === 'system' && message.type === 'search') && message.status !== 'failed'
    ));

    return sendable.map((message, index) => {
      let content = message.content;
      // Previous media can be represented as context without repeatedly
      // uploading large base64 payloads. The final outgoing message keeps it.
      if (index < sendable.length - 1 && Array.isArray(content)) {
        content = content.map((item) => {
          if (item.type === 'image_url' && item.image_url?.url?.startsWith('data:')) {
            return { type: 'text', text: '[Image attached previously]' };
          }
          if (item.type === 'input_audio' && item.input_audio?.data) {
            return { type: 'text', text: '[Voice message attached previously]' };
          }
          return item;
        });
      }
      return { role: message.role, content };
    });
  };

  const sendMessageRequest = async (messagesForRequest, clientId, failureLabel = 'message') => {
    // Captured at SEND time: the reply belongs to the conversation this
    // message was part of. If the user clears the chat while the request is
    // in flight, the epoch moves on and the reply is dropped, not appended
    // to a conversation it was never in.
    const epoch = conversationEpochRef.current;
    setIsLoading(true);
    setIsTyping(false);

    try {
      const response = await fetch(ENDPOINTS.CHAT, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ conversation: conversationForApi(messagesForRequest) })
      });

      if (handleAuthError(response)) {
        markMessageStatus(clientId, 'failed');
        return;
      }
      if (!response.ok) throw new Error(`Server error: ${response.status}`);

      const data = await response.json();
      const assistantMessages = data.messages || [data.response || "Sorry, something went wrong."];

      // A completed response proves the server received and processed the
      // outgoing message. Only now is the double-check truthful.
      markMessageStatus(clientId, 'delivered');

      if (data.transcription) {
        setConversation((previous) => previous.map((message) => (
          message.clientId === clientId ? { ...message, transcription: data.transcription } : message
        )));
      }

      setIsLoading(false);

      if (data.searched && epoch === conversationEpochRef.current) {
        setIsSearching(true);
        await delay(800);
        setIsSearching(false);
        if (epoch !== conversationEpochRef.current) return;
        setConversation((previous) => [...previous, {
          clientId: createClientId(),
          role: 'system',
          type: 'search',
          content: 'Searched the web',
          timestamp: new Date().toISOString()
        }]);
        await delay(300);
      }

      for (let index = 0; index < assistantMessages.length; index += 1) {
        if (epoch !== conversationEpochRef.current) break;
        setIsTyping(true);
        const message = assistantMessages[index];
        const messageLength = typeof message === 'string' ? message.length : 0;
        const baseDelay = 300 + Math.random() * 500;
        const lengthBonus = Math.min(messageLength * 5, 300);
        await delay(baseDelay + lengthBonus);
        setIsTyping(false);
        if (epoch !== conversationEpochRef.current) break;
        setConversation((previous) => [...previous, {
          clientId: createClientId(),
          role: 'assistant',
          content: message,
          timestamp: new Date().toISOString()
        }]);
        if (index < assistantMessages.length - 1) await delay(150);
      }
    } catch (error) {
      console.error('Error:', error);
      markMessageStatus(clientId, 'failed');
      toast.error(`Failed to send ${failureLabel}`);
    } finally {
      setIsLoading(false);
      setIsTyping(false);
      setIsSearching(false);
    }
  };

  const sendVoiceMessage = async (audioData) => {
    const userContent = [
      { type: "text", text: `[Voice message - ${audioData.duration}s]` },
      {
        type: "input_audio",
        input_audio: {
          data: audioData.base64,
          format: audioData.format
        }
      }
    ];

    const newUserMessage = {
      clientId: createClientId(),
      role: "user",
      content: userContent,
      displayText: `Voice message (${audioData.duration}s)`,
      audioDuration: audioData.duration,
      timestamp: new Date().toISOString(),
      status: 'sending'
    };

    const updatedConversation = [...conversation, newUserMessage];
    setConversation(updatedConversation);
    await sendMessageRequest(updatedConversation, newUserMessage.clientId, 'voice message');
  };

  const buildMessageContent = () => {
    const content = [];

    if (inputMessage.trim()) {
      content.push({ type: "text", text: inputMessage.trim() });
    }

    if (selectedImage) {
      content.push({
        type: "image_url",
        image_url: { url: `data:image/jpeg;base64,${selectedImage.base64}` }
      });
    }

    if (content.length === 1 && content[0].type === "text") {
      return inputMessage.trim();
    }

    return content;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading || (!inputMessage.trim() && !selectedImage)) return;

    const messageContent = buildMessageContent();

    const newUserMessage = {
      clientId: createClientId(),
      role: "user",
      content: messageContent,
      timestamp: new Date().toISOString(),
      status: 'sending',
      ...(typeof messageContent !== 'string' && { displayText: inputMessage.trim() || 'Sent an image' })
    };

    const updatedConversation = [...conversation, newUserMessage];
    setConversation(updatedConversation);
    setInputMessage('');
    removeSelectedImage();
    await sendMessageRequest(updatedConversation, newUserMessage.clientId);
  };

  const retryMessage = async (clientId) => {
    if (isLoading) return;
    const failedIndex = conversation.findIndex((message) => message.clientId === clientId);
    if (failedIndex < 0 || conversation[failedIndex].status !== 'failed') return;

    const retryingMessage = { ...conversation[failedIndex], status: 'sending' };
    setConversation((previous) => previous.map((message) => (
      message.clientId === clientId ? retryingMessage : message
    )));

    // Retry the conversation as it stood when this message was first sent;
    // later messages must not accidentally become the prompt being retried.
    const retryConversation = [
      ...conversation.slice(0, failedIndex),
      retryingMessage
    ];
    const isVoice = Array.isArray(retryingMessage.content)
      && retryingMessage.content.some((item) => item.type === 'input_audio');
    await sendMessageRequest(retryConversation, clientId, isVoice ? 'voice message' : 'message');
  };

  const displayMessages = conversation.filter(
    (msg) => msg.role === "user" || msg.role === "assistant" || (msg.role === "system" && msg.type === "search")
  );

  return (
    <>
    <div ref={chatBackgroundRef} className="relative w-full h-[var(--app-height,100dvh)] bg-[#ECE5DD] shadow-xl overflow-hidden flex flex-col mx-auto sm:h-dvh sm:max-w-2xl overflow-x-hidden">
      {/* 
        Mobile Fix: layout is now relative to the fixed body.
        overflow-x-hidden prevents side-wiggling.
      */}
      {/* WhatsApp-style Header */}
      <div className="shrink-0 bg-[#075E54] px-4 py-3 text-white flex items-center justify-between" style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full transition-colors hover:bg-[#064E46]"
            aria-label="Back"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>

          {/* Avatar */}
          <img
            src="/profilepic.jpeg"
            alt="Nav"
            className="w-10 h-10 rounded-full object-cover"
          />

          <div className="flex-1">
            <h1 className="text-base font-semibold">AI Navjeev</h1>
            <p className="text-xs text-[#8DCFC7] flex items-center gap-1 chat-subtitle">
              {isSearching ? (
                'searching the web...'
              ) : isTyping || isLoading ? (
                'typing...'
              ) : (
                <><span className="inline-block w-2 h-2 bg-[#25D366] rounded-full"></span> Online</>
              )}
            </p>
          </div>
        </div>

        <button
          onClick={handleClearHistory}
          className="p-3 hover:bg-[#064E46] rounded-full transition-colors"
          title="Clear Conversation"
          aria-label="Clear Conversation"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>

      {/* Chat window with WhatsApp wallpaper pattern */}
      <div
        ref={messagesViewportRef}
        data-testid="message-history"
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-2 space-y-1"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4cfc4' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundColor: '#ECE5DD'
        }}
        onScroll={handleScroll}
      >
        {displayMessages.map((msg, index) => {
          const previousMessage = displayMessages[index - 1];
          const showDateSeparator = Boolean(
            msg.timestamp
            && dayKey(msg.timestamp) !== dayKey(previousMessage?.timestamp)
          );
          const dateSeparator = showDateSeparator ? (
            <div className="sticky top-1 z-10 flex justify-center py-1" data-testid="date-separator">
              <span className="rounded-lg bg-[#E1F3FB] px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-[#54656F] shadow-sm">
                {formatDay(msg.timestamp)}
              </span>
            </div>
          ) : null;

          // Search indicator pill (system message)
          if (msg.role === 'system' && msg.type === 'search') {
            return (
              <React.Fragment key={msg.clientId || index}>
                {dateSeparator}
                <div className="flex justify-center mb-1">
                  <div className="inline-flex items-center gap-1.5 bg-[#E2D6CA] text-[#54656F] text-xs px-3 py-1 rounded-full shadow-sm">
                    <Search className="w-3 h-3" />
                    <span>{msg.content}</span>
                  </div>
                </div>
              </React.Fragment>
            );
          }

          const displayText = msg.displayText || (typeof msg.content === 'string' ? msg.content : '');
          const isUser = msg.role === 'user';
          const timestamp = msg.timestamp ? formatTime(msg.timestamp) : '';

          return (
            <React.Fragment key={msg.clientId || index}>
              {dateSeparator}
              <div
                className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-1`}
              >
                <div
                  className={`relative max-w-[85%] rounded-lg shadow-sm ${isUser
                    ? 'bg-[#D9FDD3] rounded-tr-none'
                    : 'bg-white rounded-tl-none'
                    }`}
                  {...(!isUser ? { onClick: () => handleMessageDoubleTap(index) } : {})}
                >
                  {/* Message tail */}
                  <div
                    className={`absolute top-0 w-3 h-3 ${isUser
                      ? 'right-0 -mr-2 border-l-8 border-l-[#D9FDD3] border-t-8 border-t-transparent border-b-8 border-b-transparent'
                      : 'left-0 -ml-2 border-r-8 border-r-white border-t-8 border-t-transparent border-b-8 border-b-transparent'
                      }`}
                    style={{ top: '0px' }}
                  />

                  {/* Image if present */}
                  {getImageFromContent(msg.content) && (
                    <img
                      src={getImageFromContent(msg.content)}
                      alt="Sent"
                      className="max-w-full max-h-64 object-cover rounded-t-lg"
                    />
                  )}

                  {/* Message content */}
                  <div className="px-3 py-1.5">
                    {msg.displayText?.includes('Voice message') ? (
                      <VoiceMessagePlayer
                        audioBase64={getAudioFromContent(msg.content)}
                        duration={msg.audioDuration || '?'}
                        transcription={msg.transcription}
                        audioFormat={getAudioFormatFromContent(msg.content)}
                      />
                    ) : displayText && (
                      <p className="text-[15px] leading-5 text-[#111B21] break-words whitespace-pre-wrap">
                        {displayText}
                      </p>
                    )}

                    {/* Timestamp and truthful delivery state */}
                    <div className={`flex items-center justify-end gap-1 mt-0.5 ${getImageFromContent(msg.content) && !displayText ? '-mt-6 relative z-10 pr-1' : ''}`}>
                      <span className={`text-[11px] ${getImageFromContent(msg.content) && !displayText ? 'text-white' : 'text-[#667781]'}`}>
                        {timestamp}
                      </span>
                      {isUser && <OutgoingMessageStatus status={msg.status} />}
                    </div>

                    {isUser && msg.status === 'failed' && (
                      <button
                        type="button"
                        onClick={() => retryMessage(msg.clientId)}
                        disabled={isLoading}
                        className="mt-1 flex min-h-[44px] w-full items-center justify-end gap-1.5 border-t border-[#C5D8C2] pt-1 text-xs font-semibold text-[#B42318] disabled:opacity-50"
                        aria-label={`Retry ${displayText || 'message'}`}
                      >
                        <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                        Not sent · Retry
                      </button>
                    )}
                  </div>

                  {/* Reaction badge */}
                  {reactions[index] && (
                    <div className="absolute -bottom-3 right-2 bg-white rounded-full px-1.5 py-0.5 shadow-sm border text-sm leading-none">
                      {reactions[index]}
                    </div>
                  )}

                  {/* Reaction picker */}
                  {reactionPickerIdx === index && !isUser && (
                    <div className="absolute -bottom-10 left-0 bg-white rounded-full shadow-lg px-2 py-1 flex gap-1 z-20">
                      {['\u2764\uFE0F', '\uD83D\uDE02', '\uD83D\uDC4D', '\uD83D\uDD25'].map(emoji => (
                        <button
                          key={emoji}
                          onClick={(e) => { e.stopPropagation(); addReaction(index, emoji); }}
                          className="text-xl hover:scale-125 transition-transform p-1"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </React.Fragment>
          );
        })}

        {/* Typing indicator */}
        {isTyping && (
          <div className="flex justify-start mb-1">
            <TypingIndicator />
          </div>
        )}
        <div ref={messagesEndRef} />

        {/* Scroll to bottom button - sticky inside scroll container */}
        {showScrollButton && (
          <button
            type="button"
            onClick={() => scrollToBottom('smooth')}
            className="sticky bottom-2 ml-auto mr-2 flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full bg-white text-[#667781] shadow-lg transition-colors hover:bg-gray-50 z-10"
            aria-label="Jump to latest message"
          >
            <ChevronDown className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Image preview bar */}
      {selectedImage && (
        <div className="shrink-0 px-4 py-2 bg-[#F0F2F5] flex items-center gap-3">
          <div className="relative">
            <img
              src={selectedImage.preview}
              alt="Preview"
              className="h-16 w-16 object-cover rounded-lg"
            />
            <button
              type="button"
              onClick={removeSelectedImage}
              className="absolute -top-2 -right-2 bg-[#075E54] text-white rounded-full p-2 hover:bg-[#064E46] transition-colors"
              aria-label="Remove attached image"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <span className="text-sm text-[#667781]">Image attached</span>
        </div>
      )}

      {/* Recording indicator */}
      {isRecording && (
        <div className="shrink-0 px-4 py-3 bg-[#F0F2F5] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            <span className="text-[#075E54] font-medium">Recording {formattedDuration}</span>
          </div>
          <button
            type="button"
            onClick={cancelRecording}
            className="text-red-500 hover:text-red-600 text-sm font-medium px-4 py-3 min-h-[44px] flex items-center"
          >
            Cancel
          </button>
        </div>
      )}

      {/* WhatsApp-style Input area */}
      <form onSubmit={handleSubmit} className="shrink-0 px-2 py-2 bg-[#F0F2F5]" style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleImageSelect}
          accept="image/*"
          className="hidden"
        />

        <div className="flex items-end gap-2">
          {/* Attachment button */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading || isRecording}
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-[#54656F] transition-colors hover:bg-white hover:text-[#075E54] disabled:opacity-50"
            title="Attach image"
            aria-label="Attach image"
          >
            <Paperclip className="h-6 w-6" />
          </button>

          {/* Input field */}
          <div className="flex min-h-[44px] flex-1 items-end rounded-3xl bg-white px-4 py-2.5">
            <textarea
              ref={textareaRef}
              rows={1}
              value={inputMessage}
              onChange={handleInputChange}
              onKeyDown={handleComposerKeyDown}
              onFocus={() => setTimeout(() => scrollToBottom('auto'), 300)}
              placeholder={isRecording ? "Recording..." : "Type a message"}
              disabled={isRecording}
              aria-label="Message"
              className="max-h-[120px] min-h-[24px] flex-1 resize-none bg-transparent text-base leading-6 text-[#111B21] placeholder-[#667781] focus:outline-none disabled:bg-transparent"
            />
          </div>

          {/* Mic or Send button */}
          {!inputMessage.trim() && !selectedImage ? (
            <button
              type="button"
              onClick={handleMicClick}
              disabled={isLoading}
              className={`flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full transition-colors ${isRecording
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-[#00A884] text-white hover:bg-[#008F72]'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              title={isRecording ? "Stop recording" : "Record voice message"}
              aria-label={isRecording ? "Stop recording" : "Record voice message"}
            >
              {isLoading ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : isRecording ? (
                <Square className="h-6 w-6" />
              ) : (
                <Mic className="h-6 w-6" />
              )}
            </button>
          ) : (
            <button
              type="submit"
              disabled={isLoading || isRecording}
              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full bg-[#00A884] text-white transition-colors hover:bg-[#008F72] disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Send message"
            >
              {isLoading ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : (
                <Send className="h-6 w-6" />
              )}
            </button>
          )}
        </div>
      </form>

      {/* WhatsApp CSS custom classes */}
      <style>{`
        .wa-bubble-in {
          background-color: white;
          box-shadow: 0 1px 0.5px rgba(0, 0, 0, 0.13);
        }
      `}</style>

    </div>

      {/* Clear-conversation confirmation. Outside the background div, because
          useDialogA11y marks that div inert while this is open -- a dialog
          inside it could not take focus. */}
      {showClearConfirm && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          role="presentation"
          onClick={() => setShowClearConfirm(false)}
        >
          <div
            ref={clearDialogRef}
            onKeyDown={clearDialogKeyDown}
            onClick={(e) => e.stopPropagation()}
            className="mx-4 w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="clear-chat-title"
            aria-describedby="clear-chat-description"
          >
            <h3 id="clear-chat-title" data-dialog-heading tabIndex={-1} className="mb-2 text-lg font-semibold text-[#111B21]">Clear chat?</h3>
            <p id="clear-chat-description" className="mb-5 text-sm leading-6 text-[#54656F]">All messages in this chat will be deleted. This can’t be undone.</p>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowClearConfirm(false)}
                className="min-h-[44px] rounded-full px-5 py-2 text-sm font-semibold text-[#008069] hover:bg-[#F0F2F5]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmClearHistory}
                className="min-h-[44px] rounded-full bg-[#008069] px-5 py-2 text-sm font-semibold text-white hover:bg-[#006B59]"
              >
                Clear chat
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ModernChatInterface;
